import type { Header } from '@/payload-types'

/** Matches seed / typical first nav label when CMS has not saved dropdown rows yet. */
const DEFAULT_FIRST_ITEM_DROPDOWN_LABELS = [
  'Our Remodeling Services',
  'Remodeling Services',
]

const DEFAULT_DROPDOWN_LINKS: NonNullable<
  NonNullable<Header['navItems']>[number]['dropdownItems']
> = [
  {
    link: {
      type: 'custom',
      label: 'Bathroom Remodels',
      url: '/bathroom-remodels',
    },
  },
  {
    link: {
      type: 'custom',
      label: 'Kitchen Remodels',
      url: '/kitchen-remodels',
    },
  },
  {
    link: {
      type: 'custom',
      label: 'Home Remodels',
      url: '/home-remodels',
    },
  },
  {
    link: {
      type: 'custom',
      label: 'Additions & ADUs',
      url: '/additions-adus',
    },
  },
]

/** Used when the Header global has no nav rows yet (empty CMS array). */
const FALLBACK_NAV_ITEMS: NonNullable<Header['navItems']> = [
  {
    link: {
      type: 'custom',
      label: 'Our Remodeling Services',
      url: '/kitchen-remodels',
      newTab: false,
    },
    dropdownItems: DEFAULT_DROPDOWN_LINKS,
  },
  {
    link: {
      type: 'custom',
      label: 'Our Projects',
      url: '/crafted-projects',
      newTab: false,
    },
  },
  {
    link: {
      type: 'custom',
      label: 'Our Craft',
      url: '/our-process',
      newTab: false,
    },
    dropdownItems: [
      { link: { type: 'custom', label: 'Our Process', url: '/our-process' } },
      { link: { type: 'custom', label: 'About Us', url: '/about-us' } },
      { link: { type: 'custom', label: "Craft's Magazine", url: '/blogs' } },
    ],
  },
  {
    link: {
      type: 'custom',
      label: 'Contact Us',
      url: '/contact-us',
      newTab: false,
    },
  },
]

function firstNavLabelMatchesDefault(label: string | null | undefined): boolean {
  const t = label?.trim() ?? ''
  if (!t) return false
  return DEFAULT_FIRST_ITEM_DROPDOWN_LABELS.some(
    (d) => t === d || t.toLowerCase().includes('remodeling service'),
  )
}

/**
 * If the first header nav item matches the default “services” label but has no
 * `dropdownItems` in the database yet, use the standard submenu so hover works
 * without an extra CMS save (after schema add).
 */
export function withDefaultHeaderDropdowns(data: Header): Header {
  const working: Header =
    data.navItems?.length && data.navItems.length > 0
      ? data
      : { ...data, navItems: FALLBACK_NAV_ITEMS }

  const navItems = working.navItems
  if (!navItems?.length) return working

  const first = navItems[0]
  if (!first?.link) return working
  if (first.dropdownItems && first.dropdownItems.length > 0) return working
  if (!firstNavLabelMatchesDefault(first.link.label)) return working

  const nextItems = [...navItems]
  nextItems[0] = {
    ...first,
    dropdownItems: DEFAULT_DROPDOWN_LINKS,
  }

  return { ...working, navItems: nextItems }
}
